import { UnitsApiService } from '@api_patient/units_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { shouldShowConditionInfoScreen } from '@component/condition_info_screen.component';
import { RouterService } from '@cortex-shim/router.service';
import { getCompletedInformationGathering } from '@feature/information-gathering/information-gathering-navigation';
import { getAbsoluteRoute } from '@module/get_absolute_route';
import { isAbsolute } from '@module/is_absolute';
import { signal } from '@preact/signals-react';
import { type ClientConditionApiModel, ReceptionConditionInfoScreenFlow, type UnitConfigApiModel } from '@visiba/api-vcs-client-v2';
import { ControllerProtocol, Injectable, Scopes } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { safeNumberParse } from '@visiba/std';
import { CareNavigationService } from './care_navigation.service';

export enum CareNavigationScreen {
	ReasonForContact = 'reason-for-contact',
	DescribeMatter = 'describe-your-matter',
	Loading = 'loading',
	Done = 'done',
}

export type CareNavigationFlowState =
	| { state: 'loading-initial' }
	| { state: 'select-reason-for-contact'; reasonForContacts: ClientConditionApiModel[] }
	| { state: 'info-screen'; reasonForContact: ClientConditionApiModel; reasonForContacts: ClientConditionApiModel[] }
	| { state: 'describe-matter'; reasonForContact: ClientConditionApiModel | null }
	| { state: 'starting-navigation' }
	| { state: 'done' }
	| { state: 'error' };

@Injectable({
	scope: Scopes.Scoped,
})
export class ViewCareNavigationController implements ControllerProtocol {
	public flowState = signal<CareNavigationFlowState>({ state: 'loading-initial' });
	public selectedReasonForContact = signal<ClientConditionApiModel | null>(null);
	public descriptionOfMatter = signal('');
	public screenState = signal<'idle' | 'busy'>('idle');
	public sourceButtonId: number | null;

	private readonly logger = new Logger('ViewCareNavigationController');
	private readonly routeBase: string;
	private readonly predefinedReasonForContactIds: number[] | null;
	private readonly showDescriptionOfMatter: boolean;

	constructor(
		private readonly routerService: RouterService,
		private readonly unitService: UnitService,
		private readonly universalConfig: UniversalConfig,
		private readonly unitsApiService: UnitsApiService,
		private readonly analyticService: AnalyticService,
		private readonly careNavigationService: CareNavigationService,
	) {
		const unit = this.unitService.getUnitNodeInTree(this.universalConfig.selectedUnitId);
		this.routeBase = unit.routeWithoutLicenceholder;
		this.predefinedReasonForContactIds =
			new URLSearchParams(window.location.search)
				.get('rfv')
				?.split(',')
				.map((id) => safeNumberParse(id))
				.filter((id): id is number => id != null) ?? null;
		this.showDescriptionOfMatter = new URLSearchParams(window.location.search).get('dom') !== '-';
		this.sourceButtonId = safeNumberParse(new URLSearchParams(window.location.search).get('buttonId') ?? '');
		const searchParams = new URLSearchParams(window.location.search);
		const patientNin = searchParams.get('patientNin') || searchParams.get('nin') || null;

		if (patientNin !== null) {
			// An explicit URL subject replaces the complete subject context. Its date of birth may not be known yet.
			this.careNavigationService.setCurrentPatientDateOfBirth(null);
			this.careNavigationService.setCurrentPatientNin(patientNin);
		} else if (!this.careNavigationService.shouldPreserveSubjectContext()) {
			// A new journey without the handoff marker clears stale subject state.
			this.careNavigationService.setCurrentPatientNin(null);
		}
	}

	async whenMount(preloadedUnitConfig?: UnitConfigApiModel) {
		this.analyticService.trackPageView({ customTitle: 'Care navigation' });
		this.flowState.value = { state: 'loading-initial' };

		const result = await this.fetchReasonForContacts(preloadedUnitConfig);
		if (result.err) {
			this.flowState.value = { state: 'error' };

			return;
		}

		const filteredReasonForContacts = this.filterReasonForContacts(result.ok);

		if (filteredReasonForContacts.length > 0) {
			this.flowState.value = {
				state: 'select-reason-for-contact',
				reasonForContacts: filteredReasonForContacts,
			};
			this.goToScreen(CareNavigationScreen.ReasonForContact, { replace: true });

			return;
		}

		if (this.showDescriptionOfMatter === false) {
			await this.startNavigation();

			return;
		}

		this.selectedReasonForContact.value = null;
		this.flowState.value = {
			state: 'describe-matter',
			reasonForContact: null,
		};
		this.goToScreen(CareNavigationScreen.DescribeMatter, { replace: true });
	}

	public goToScreen(screen: CareNavigationScreen, options: { replace?: boolean } = {}) {
		const url = [this.routeBase, 'navigation', screen].filter(Boolean).join('/');

		this.routerService.navigateByUrl(this.withCurrentCareNavigationSearch(getAbsoluteRoute(url)), {
			replace: options.replace,
			state: this.routerService.history.location.state,
		});
	}

	public getDescribeMatterRoute() {
		const screen = this.showDescriptionOfMatter === false ? CareNavigationScreen.Loading : CareNavigationScreen.DescribeMatter;
		const url = new URL(getAbsoluteRoute([this.routeBase, 'navigation', screen].filter(Boolean).join('/')), window.location.origin);

		return this.withCurrentCareNavigationSearch(`${url.pathname}${url.search}`);
	}

	public getPatientNin(): string | null {
		return this.careNavigationService.getCurrentPatientNin();
	}

	public goToReceptionHome() {
		// Client-side routerService.navigateByUrl leaves this flow's route tree stuck rendered
		// after leaving a flow route (e.g. this care navigation flow); a real URL change reliably resets it.
		// The router's basename (licence holder alias) isn't applied to window.location, so it's prepended here.
		window.location.href = getAbsoluteRoute([this.universalConfig.alias, this.routeBase].filter(Boolean).join('/'));
	}

	public async selectReasonForContact(reasonForContact: ClientConditionApiModel) {
		this.selectedReasonForContact.value = reasonForContact;
		this.analyticService.trackEvent(AnalyticCategories.RedRobin, 'Select reason for contact', `${reasonForContact.Id}`);

		if (shouldShowConditionInfoScreen(reasonForContact, ReceptionConditionInfoScreenFlow.Navigation)) {
			const currentState = this.flowState.peek();
			this.flowState.value = {
				state: 'info-screen',
				reasonForContact,
				reasonForContacts: currentState.state === 'select-reason-for-contact' ? currentState.reasonForContacts : [],
			};

			return;
		}

		if (this.showDescriptionOfMatter === false) {
			await this.startNavigation();

			return;
		}

		this.flowState.value = {
			state: 'describe-matter',
			reasonForContact,
		};
		this.goToScreen(CareNavigationScreen.DescribeMatter);
	}

	public setDescriptionOfMatter(value: string) {
		this.descriptionOfMatter.value = value;
	}

	public backToReasonForContactSelection() {
		const currentState = this.flowState.peek();
		this.selectedReasonForContact.value = null;

		this.flowState.value = {
			state: 'select-reason-for-contact',
			reasonForContacts:
				currentState.state === 'select-reason-for-contact' || currentState.state === 'info-screen'
					? currentState.reasonForContacts
					: [],
		};
		this.goToScreen(CareNavigationScreen.ReasonForContact, { replace: true });
	}

	public continueAfterInfoScreen() {
		const reasonForContact = this.selectedReasonForContact.peek();

		if (!reasonForContact) {
			this.backToReasonForContactSelection();
			return;
		}

		if (this.showDescriptionOfMatter === false) {
			void this.startNavigation();
			return;
		}

		this.flowState.value = {
			state: 'describe-matter',
			reasonForContact,
		};
		this.goToScreen(CareNavigationScreen.DescribeMatter);
	}

	public async startNavigation(): AsyncResult<void> {
		const selectedReasonForContact = this.selectedReasonForContact.peek();

		this.screenState.value = 'busy';
		this.flowState.value = { state: 'starting-navigation' };
		this.goToScreen(CareNavigationScreen.Loading, { replace: true });
		const informationGatheringSubmissionId =
			getCompletedInformationGathering(this.routerService.history.location.state)?.submissionId ?? undefined;

		const result = await this.careNavigationService.startNavigation({
			informationGatheringSubmissionId,
			descriptionOfMatter: this.descriptionOfMatter.peek(),
			reasonForContact: selectedReasonForContact,
			sourceButtonId: this.sourceButtonId,
			asUnverifiedRepresentativeForNin: informationGatheringSubmissionId ? null : this.careNavigationService.getCurrentPatientNin(),
		});

		this.screenState.value = 'idle';

		if (result.err) {
			this.flowState.value = { state: 'error' };
			return Err(result.err);
		}

		const deepLink = result.ok.deepLink;
		if (!deepLink) {
			this.flowState.value = { state: 'done' };
			this.goToScreen(CareNavigationScreen.Done, { replace: true });
			return Ok();
		}

		if (result.ok.isAbsolute) {
			window.location.href = deepLink;
			return Ok();
		}

		this.routerService.navigateByUrl(getAbsoluteRoute(deepLink), {
			state: this.routerService.history.location.state,
		});

		return Ok();
	}

	private async fetchReasonForContacts(preloadedUnitConfig?: UnitConfigApiModel): AsyncResult<ClientConditionApiModel[]> {
		this.logger.logInformation('Get reason for contacts');

		let unitConfig = preloadedUnitConfig;
		if (unitConfig === undefined) {
			const unitConfigResult = await this.unitsApiService.getUnitConfig(this.universalConfig.selectedUnitId);
			if (unitConfigResult.err) {
				return Err(unitConfigResult.err);
			}
			unitConfig = unitConfigResult.ok.data;
		}

		if (unitConfig.Offer?.HasConditionWorkflow === false) {
			return Ok([]);
		}

		const conditionsResult = await this.unitsApiService.getConditions(this.universalConfig.selectedUnitId);
		if (conditionsResult.err) {
			this.logger.logError(
				'User tried to get reason for contacts: {message} {statusCode}',
				conditionsResult.err.message,
				conditionsResult.err.statusCode,
			);

			return Err(conditionsResult.err);
		}

		return Ok(conditionsResult.ok.data);
	}

	private filterReasonForContacts(reasonForContacts: ClientConditionApiModel[]): ClientConditionApiModel[] {
		const predefinedReasonForContactIds = this.predefinedReasonForContactIds;

		if (predefinedReasonForContactIds == null || predefinedReasonForContactIds.length === 0) {
			return [];
		}

		return reasonForContacts.filter((reasonForContact) => predefinedReasonForContactIds.includes(reasonForContact.Id));
	}

	private withCurrentCareNavigationSearch(route: string): string {
		const routeUrl = new URL(route, window.location.origin);
		const currentSearch = new URLSearchParams(window.location.search);

		for (const [key, value] of currentSearch.entries()) {
			routeUrl.searchParams.set(key, value);
		}

		return `${routeUrl.pathname}${routeUrl.search}${routeUrl.hash}`;
	}
}
